import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, showToast } from "@xpense/ui";
import { api, type AuthSessionResponse } from "../api/client";

interface GoogleAuthCallbackProps {
  onAuthenticated: (token: string) => void;
}

export default function GoogleAuthCallback({ onAuthenticated }: GoogleAuthCallbackProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const exchangeStarted = useRef(false);
  const [status, setStatus] = useState<"processing" | "error">("processing");
  const [message, setMessage] = useState("Signing you in with Google...");

  useEffect(() => {
    if (exchangeStarted.current) {
      return;
    }

    exchangeStarted.current = true;

    const error = searchParams.get("error");
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (error) {
      setStatus("error");
      setMessage(`Google sign-in was cancelled or failed: ${error}`);
      return;
    }

    if (!code || !state) {
      setStatus("error");
      setMessage("Google did not return the required sign-in details.");
      return;
    }

    const redirectUri = `${window.location.origin}/auth/google/callback`;

    api
      .post<AuthSessionResponse>("/api/v1/auth/google/callback", {
        code,
        state,
        redirect_uri: redirectUri,
      })
      .then((response) => {
        onAuthenticated(response.token);
        showToast("Signed in with Google", "success");
        navigate("/", { replace: true });
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err instanceof Error ? err.message : "Failed to complete Google sign-in.");
      });
  }, [navigate, onAuthenticated, searchParams]);

  if (status === "processing") {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", flexDirection: "column", gap: "var(--xpn-space-lg)", background: "var(--xpn-bg)" }}>
        <div className="xpn-skeleton xpn-skeleton--card" style={{ width: 260, height: 48 }} />
        <p className="xpn-text-body-md xpn-text-muted">{message}</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", flexDirection: "column", gap: "var(--xpn-space-lg)", background: "var(--xpn-bg)", padding: "var(--xpn-space-xl)" }}>
      <p className="xpn-text-body-md xpn-text-danger">{message}</p>
      <Button variant="secondary" onClick={() => navigate("/", { replace: true })}>
        Back to Login
      </Button>
    </div>
  );
}
